import React, { useEffect } from 'react';
import { AppConfigs, AppSkinProps, CriteriaConstraint } from '../../../../types/decisions';
import Slider from '../../../slider/Slider';
import { TriangleSlider } from '../../../slider';
import { Button, FormControl, IconButton, MenuItem, Select, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import NumberField from '../../../number-field/number-field';

const AppCriteriaTypeNumber = ({
    min,
    max,
    importance,
    value,
    hideValues,
    hideImportance,
    onSliderChange,
    onTradeoffValueChange,
    showAddConstraint,
    criteriaConstraints,
    setCriteriaConstraints,
    id,
    variant,
    isRange = false,
    disabled,
}: {
    disabled?: boolean;
    importance: number,
    value: number | number[],
    min: number,
    max: number,
    showAddConstraint?: boolean,
    hideValues?: boolean,
    hideImportance?: boolean,
    onSliderChange: AppSkinProps['onSliderChange'];
    onTradeoffValueChange: (value: any, id: string) => void,
    id: string,
    criteriaConstraints: {
        [key: string]: CriteriaConstraint[]
    },
    setCriteriaConstraints: (value: (((prevState: {
        [p: string]: CriteriaConstraint[]
    }) => {
        [p: string]: CriteriaConstraint[]
    }) | {
                                         [p: string]: CriteriaConstraint[]
                                     })) => void;
    variant?: AppConfigs['criteriaSettings']['number'];
    isRange?: boolean;
}) => {

    useEffect(() => {
        if (!criteriaConstraints[id]) {
            setCriteriaConstraints(criteriaConstraints => (
                {
                    ...criteriaConstraints,
                    [id]: [],
                }
            ));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onConstraintChange = (value: string, index: number, key: 'value' | 'rule') => {
        setCriteriaConstraints((constraints) => {
            const updatedConstraints = constraints[id].map((constraint, idx) => {
                if (idx !== index) {
                    return constraint;
                }

                return {
                    ...constraint,
                    [key]: value,
                };
            });

            return {
                ...constraints,
                [id]: updatedConstraints,
            };
        });
    };

    const onConstraintOperatorChange = (value: 'and' | 'or', index: number) => {
        setCriteriaConstraints((constraints) => {
            const updatedConstraints = constraints[id].map((constraint, idx) => {
                if (idx !== index) {
                    return constraint;
                }

                return {
                    ...constraint,
                    operator: value,
                };
            });

            return {
                ...constraints,
                [id]: updatedConstraints,
            };
        });
    };

    const onAddConstraint = () => {
        setCriteriaConstraints(constraints => (
            {
                ...constraints,
                [id]: [ ...constraints[id], {
                    value: '',
                    rule: '=',
                    operator: constraints[id].length > 0 ? 'and' : null,
                } ],
            }
        ));
    };

    const onDeleteConstraint = (index: number) => {
        setCriteriaConstraints(constraints => {
            const filtered = constraints[id].filter((_, idx) => idx !== index);
            if (!filtered.length) {
                return {
                    ...constraints,
                    [id]: [],
                };
            }

            filtered[0] = {
                ...filtered[0],
                operator: null,
            };

            return {
                ...constraints,
                [id]: filtered,
            };
        });
    };

    return (
        <div>
            {hideValues ? null : (
                <div style={{ marginBottom: 10 }}>
                    {variant === 'Field' ? (
                        <NumberField
                            id={id}
                            value={value}
                            onTradeoffValueChange={onTradeoffValueChange}
                            isRange={isRange}
                            disabled={disabled}
                            min={min}
                            max={max}
                        />
                    ) : (
                        <div style={{ padding: '0 1rem' }}>
                            <Slider
                                key={Array.isArray(value) ? value.join('_') : value}
                                min={min}
                                max={max}
                                defaultValue={value}
                                hideThumbButtons
                                leftText={min + ''}
                                rightText={max + ''}
                                onChangeCommitted={(_, value) => onTradeoffValueChange(value, id)}
                                valueLabelDisplay={hideImportance ? 'auto' : 'on'}
                                disabled={disabled}
                            />
                        </div>
                    )}
                </div>
            )}
            {hideImportance ? null : (
                <div>
                    <TriangleSlider
                        min={0}
                        max={100}
                        step={1}
                        value={importance}
                        leftText="Not Important"
                        rightText="Really Important"
                        onChange={(_, value) => onSliderChange(value as number, id)}
                        disabled={disabled}
                    />
                </div>
            )}
            {!showAddConstraint ? null : (
                <div style={{ marginTop: 20 }}>
                    <div style={{ display: criteriaConstraints[id].length > 0 ? 'block' : 'none' }}>
                        <FormControl sx={{ my: 2, minWidth: 120 }} size="small" disabled={disabled}>
                            {criteriaConstraints[id].map((constraint, index) => (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        alignItems: 'center',
                                        marginBottom: 16,
                                    }}
                                    key={id + '_' + index}
                                >
                                    {constraint.operator && (
                                        <Select
                                            style={{ marginRight: 16 }}
                                            variant="outlined"
                                            value={constraint.operator}
                                            onChange={(e) => onConstraintOperatorChange(e.target.value as ('and' | 'or'), index)}
                                        >
                                            <MenuItem value="and">And</MenuItem>
                                            <MenuItem value="or">Or</MenuItem>
                                        </Select>
                                    )}
                                    <Select
                                        variant="outlined"
                                        value={constraint.rule}
                                        onChange={(e) => onConstraintChange(e.target.value, index, 'rule')}
                                    >
                                        <MenuItem value="=">Is equal to</MenuItem>
                                        <MenuItem value="!=">Is not equal to</MenuItem>
                                        <MenuItem value=">">Is higher than</MenuItem>
                                        <MenuItem value="<">Is less than</MenuItem>
                                    </Select>
                                    <TextField
                                        style={{ marginLeft: 16 }}
                                        value={constraint.value}
                                        onChange={(e) => onConstraintChange(e.target.value, index, 'value')}
                                        size="small"
                                    />
                                    <IconButton style={{ marginLeft: '.5rem' }}
                                                onClick={() => onDeleteConstraint(index)}>
                                        <CloseIcon/>
                                    </IconButton>
                                </div>
                            ))}
                        </FormControl>
                    </div>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon/>}
                        size="small"
                        onClick={onAddConstraint}
                        disabled={disabled}
                    >
                        Add Constraint
                    </Button>
                </div>
            )}
        </div>
    );
};

export default AppCriteriaTypeNumber;
